import React, { useMemo } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { capitalizeFirstLetterOfEachWord } from '../Utils/utils';

/**
 * "Pick one person from this clinic" dialog — searchable list of cards, one selected.
 *
 * Extracted from the Assign Cardiologist modal when the same picker was needed for the
 * report's Sonographer field. It owns the LOOK and the search only; who is in the list,
 * what confirming does, and the success/failure handling stay with each caller, because
 * assigning a cardiologist (moves worklist ownership, pulls Draft to Preliminary) and
 * naming a sonographer (records who scanned) are different operations that happen to
 * share a picker.
 */

export type PickablePerson = {
  _id: string;
  name?: string;
  email?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  people: PickablePerson[];
  loading?: boolean;
  /** Shown when the clinic has nobody of this role at all. */
  emptyText: string;
  /** Shown when the search term matches nobody. Receives the trimmed term. */
  noMatchText: (term: string) => string;
  search: string;
  onSearchChange: (value: string) => void;
  selectedId: string;
  onSelect: (id: string) => void;
  onConfirm: () => void;
  busy?: boolean;
  confirmLabel: string;
  busyLabel: string;
};

const initials = (name?: string) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('') || '?';

const PersonPickerDialog: React.FC<Props> = ({
  open,
  onClose,
  title,
  subtitle,
  people,
  loading = false,
  emptyText,
  noMatchText,
  search,
  onSearchChange,
  selectedId,
  onSelect,
  onConfirm,
  busy = false,
  confirmLabel,
  busyLabel,
}) => {
  // Name/email search over the fetched list — clinics with 20+ users made the plain
  // dropdown a scroll hunt.
  const visiblePeople = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return people;
    }
    return people.filter(person =>
      [person?.name, person?.email].some(field =>
        String(field || '')
          .toLowerCase()
          .includes(term)
      )
    );
  }, [people, search]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(2, 8, 15, 0.72)', backdropFilter: 'blur(3px)' },
        },
        paper: {
          sx: theme => ({
            borderRadius: 3,
            border: `1px solid ${theme.palette.cardiac.line}`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.cardiac.surface2} 0%, ${theme.palette.cardiac.surface} 100%)`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
          }),
        },
      }}
    >
      <DialogContent sx={{ pt: 3.5, pb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.5 }}>
          {subtitle}
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search by name or email"
          autoFocus
          sx={{ mt: 2.5 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 19, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Scroll area: the rows are separate cards with a gap, and the container keeps a
            stable gutter so a card edge never collides with the scrollbar. */}
        <Box
          sx={theme => ({
            mt: 2,
            maxHeight: 316,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1,
            scrollbarGutter: 'stable',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '&::-webkit-scrollbar': { width: 10 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.cardiac.lineStrong,
              borderRadius: 8,
              border: '3px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.cardiac.ink3,
            },
          })}
        >
          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
              <CircularProgress size={26} />
            </Box>
          ) : visiblePeople.length === 0 ? (
            <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.25, py: 6, px: 3 }}>
              <PersonSearchRoundedIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', textAlign: 'center' }}>
                {people.length === 0 ? emptyText : noMatchText(search.trim())}
              </Typography>
            </Box>
          ) : (
            visiblePeople.map(person => {
              const isSelected = selectedId === person?._id;
              return (
                <ButtonBase
                  key={person?._id}
                  onClick={() => onSelect(person?._id)}
                  focusRipple
                  sx={theme => ({
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    alignItems: 'center',
                    columnGap: 1.75,
                    textAlign: 'left',
                    width: '100%',
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${isSelected ? theme.palette.cardiac.teal : theme.palette.cardiac.line}`,
                    backgroundColor: isSelected
                      ? 'rgba(45, 212, 191, 0.10)'
                      : 'rgba(4, 8, 13, 0.45)',
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? 'rgba(45, 212, 191, 0.16)'
                        : 'rgba(22, 38, 59, 0.75)',
                      borderColor: isSelected
                        ? theme.palette.cardiac.teal
                        : theme.palette.cardiac.lineStrong,
                    },
                  })}
                >
                  <Avatar
                    sx={theme => ({
                      width: 38,
                      height: 38,
                      fontSize: 13.5,
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      color: isSelected ? '#04221d' : theme.palette.cardiac.ink,
                      backgroundColor: isSelected
                        ? theme.palette.cardiac.teal
                        : theme.palette.cardiac.surface2,
                      border: `1px solid ${isSelected ? 'transparent' : theme.palette.cardiac.line}`,
                    })}
                  >
                    {initials(person?.name)}
                  </Avatar>

                  <Box sx={{ minWidth: 0, display: 'grid', rowGap: 0.25 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        letterSpacing: '0.01em',
                        color: 'text.primary',
                      }}
                    >
                      {capitalizeFirstLetterOfEachWord(person?.name)}
                    </Typography>
                    {person?.email ? (
                      <Typography
                        noWrap
                        sx={theme => ({
                          fontSize: 12.5,
                          lineHeight: 1.35,
                          color: isSelected ? theme.palette.cardiac.teal : 'text.secondary',
                        })}
                      >
                        {person.email}
                      </Typography>
                    ) : null}
                  </Box>

                  <CheckCircleRoundedIcon
                    sx={theme => ({
                      fontSize: 20,
                      color: theme.palette.cardiac.teal,
                      opacity: isSelected ? 1 : 0,
                      transition: 'opacity 120ms ease',
                    })}
                  />
                </ButtonBase>
              );
            })
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={busy}
            sx={theme => ({
              borderColor: theme.palette.cardiac.lineStrong,
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.1,
              '&:hover': {
                borderColor: theme.palette.cardiac.ink3,
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
              },
            })}
          >
            No, cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={busy || !selectedId}
            startIcon={
              busy ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                />
              ) : null
            }
            sx={{ textTransform: 'none', fontWeight: 700, py: 1.1 }}
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PersonPickerDialog;
